import React, { useState, useEffect, useRef } from 'react';

/**
 * Fake progress bar that feels fast.
 *
 * Psychology: An ease-out curve (fast start, slow tail) makes users
 * perceive the wait as shorter. We rush to 60% in the first second,
 * crawl to 90% over the next 4 seconds, and snap to 100% on completion.
 * Users feel momentum immediately and stay patient through the tail.
 *
 * Props:
 *   done: boolean — snap to 100% when true
 *   label: string — text below the bar
 */
export default function VerifyingProgress({ done, label = 'Verifying...' }) {
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());
  const frameRef = useRef(null);

  useEffect(() => {
    if (done) {
      setProgress(100);
      return;
    }

    startRef.current = Date.now();

    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;

      // Ease-out curve: fast start, asymptotic approach to 90%
      // Formula: 90 * (1 - e^(-elapsed * 1.5))
      // At 0.5s: ~47%  At 1s: ~67%  At 2s: ~85%  At 4s: ~90%
      const pct = Math.min(90, 90 * (1 - Math.exp(-elapsed * 1.5)));
      setProgress(pct);

      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [done]);

  return (
    <>
      <div className="le-progress-wrap">
        <div className="le-progress-bar" style={{ width: `${progress}%` }} />
      </div>
      <span>{label}</span>
    </>
  );
}
